import { parsePaymentInfos } from 'modules/deal/components/section/payment/stripePaymentInfo';
export function roundMoney(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}
function percentOf(line) {
    if (line.percent == null) {
        return null;
    }
    const percent = Number(line.percent);
    return Number.isFinite(percent) ? percent : null;
}
/**
 * Recompute the amount of every open percent-based payment from the deal
 * total. Completed payments keep what was actually charged. The last
 * percent-based payment absorbs the rounding cent.
 *
 * Mirrors the backend (`lib/util/dealPaymentSchedule.ts`), keep both in sync.
 */
export function applyPercentPaymentAmounts(payments, total) {
    const eligible = payments
        .map((line, index) => ({ index, percent: percentOf(line) }))
        .filter((entry) => entry.percent !== null && payments[entry.index].status !== 'COMPLETED');
    if (eligible.length === 0) {
        return payments;
    }
    const dealTotal = Number(total) || 0;
    const target = roundMoney((dealTotal * eligible.reduce((sum, entry) => sum + entry.percent, 0)) / 100);
    const amounts = new Map();
    let allocated = 0;
    eligible.forEach((entry, position) => {
        const isLast = position === eligible.length - 1;
        const amount = isLast
            ? roundMoney(target - allocated)
            : roundMoney((dealTotal * entry.percent) / 100);
        allocated = roundMoney(allocated + amount);
        amounts.set(entry.index, amount);
    });
    return payments.map((line, index) => amounts.has(index) ? Object.assign(Object.assign({}, line), { amount: amounts.get(index) }) : line);
}
/**
 * Share of the total shown for a payment: the percent typed when the
 * payment follows the total, otherwise its fixed amount over the total.
 */
export function paymentPercent(payment, total) {
    const percent = percentOf(payment);
    if (percent !== null && payment.status !== 'COMPLETED') {
        return percent;
    }
    return total ? Math.round((payment.amount / total) * 10000) / 100 : 0;
}
/**
 * What is left to schedule: the deal total minus every payment amount.
 */
export function paymentScheduleBalance(payments, total) {
    return roundMoney((Number(total) || 0) -
        payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0));
}
/**
 * Bring the schedule back to the deal total by adjusting the last open
 * payment. When every payment follows the total, the last percent becomes
 * the remainder to 100 %, so the schedule keeps following the total.
 */
export function settlePaymentScheduleBalance(payments, total) {
    const balance = paymentScheduleBalance(payments, total);
    if (Math.abs(balance) < 0.005) {
        return payments;
    }
    const lastIndex = payments
        .map((payment) => payment.status !== 'COMPLETED')
        .lastIndexOf(true);
    if (lastIndex < 0) {
        return payments;
    }
    const allPercent = payments.every((payment) => payment.status !== 'COMPLETED' && percentOf(payment) !== null);
    if (allPercent) {
        const others = payments.reduce((sum, payment, index) => { var _a; return index === lastIndex ? sum : sum + ((_a = percentOf(payment)) !== null && _a !== void 0 ? _a : 0); }, 0);
        const percent = Math.round((100 - others) * 10000) / 10000;
        return applyPercentPaymentAmounts(payments.map((payment, index) => index === lastIndex ? Object.assign(Object.assign({}, payment), { percent }) : payment), total);
    }
    return payments.map((payment, index) => index === lastIndex
        ? Object.assign(Object.assign({}, payment), { amount: roundMoney(Number(payment.amount) + balance), percent: null }) : payment);
}
/**
 * A payment settled through Stripe, Mews or a store upsell. Its amount and
 * status are what the processor charged; the server ignores edits to them.
 * A payment marked completed by hand has no infos and stays editable.
 */
export function isProcessedPayment(payment) {
    const infos = parsePaymentInfos(payment.infos);
    if (infos.kind === 'upsell') {
        return true;
    }
    return payment.status === 'COMPLETED' && Object.keys(infos).length > 0;
}
